import React from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Leaf, ArrowLeft, ArrowRight, Clock, Buildings, Factory, Handshake, Sparkle } from "@phosphor-icons/react";

// Curated case studies + blog posts. In production these would come from a CMS.
const POSTS = [
  {
    slug: "cbam-hazirlik-2026",
    icon: Factory,
    category_tr: "Vaka Çalışması",
    category_en: "Case Study",
    read_tr: "6 dk okuma",
    read_en: "6 min read",
    date: "2026-02-15",
    title_tr: "500t/ay çelik ihracatçısı için CBAM hazırlığı — 6 haftalık yol haritası",
    title_en: "CBAM readiness for a 500t/mo steel exporter — a 6-week roadmap",
    excerpt_tr: "İzmir merkezli bir demir-çelik üreticisi, AB'ye Q1 2026 itibarıyla yıllık 6.000 ton ihracat yapıyordu. Enasuspro® ile 6 hafta içinde CBAM Registry entegrasyonunu tamamladık.",
    excerpt_en: "An İzmir-based steel manufacturer was shipping 6,000 tonnes/year to the EU as of Q1 2026. With Enasuspro® we completed CBAM Registry integration in 6 weeks.",
    body_tr: [
      "**Zorluk**: Müşteri, Ocak 2026 itibarıyla AB ithalatçılarından zorunlu CBAM raporu talepleri almaya başladı. Fabrikadaki 3 farklı üretim hattı için ayrı gömülü emisyon hesaplamaları gerekiyordu. Mevcut çelik üretim kapasitesi: 6.000 t/yıl, tüm ürünler CN 7206/7207/7213 altında.",
      "**Çözüm**: Enasuspro® Carbon Ledger üzerinden aşağıdaki adımları uyguladık:",
      "**Hafta 1-2**: Tesis kurulumu (3 tesis, 3 üretim hattı). ISO 14064-1 uyumlu Kapsam 1 (kok kömürü + doğal gaz) ve Kapsam 2 (satın alınan elektrik) faktörleri tanımlandı. Baz yıl 2025.",
      "**Hafta 3-4**: 12 aylık geçmiş verinin toplu içe aktarımı (CSV). Doğrudan emisyon 2.15 tCO₂e/t, dolaylı 0.42 tCO₂e/t olarak ölçüldü — AB Annex III default değerlerinden (2.0 + 0.4) daha yüksek, bu da müşterinin gerçek tesis verisi vermesini gerekli kıldı.",
      "**Hafta 5**: CBAM sayfasında ilk çeyreklik (Q1 2026) beyanları girildi. 1.500 t · Türkiye menşeli, ödemede karbon fiyatı 0 (Türkiye ETS pilot henüz aktif değil).",
      "**Hafta 6**: XML export'u AB CBAM Registry portalına test yüklemesi yapıldı. PDF olarak müşteri yönetim kuruluna sunum. Sonuç: **€8,632 CBAM yükümlülüğü** — 2026 %2.5 phase-in ile. 2030'da bu tutar 20 katına çıkacak (€172,640/çeyrek).",
      "**Sonuç ve içgörü**: Müşteri 2027 için elektrik arz sözleşmesini yeşil PPA'ya çevirmeye karar verdi — hesaplama, yenilenebilir elektrik ile toplam gömülü emisyonu **%18 azaltabileceğini** gösterdi. Bu da 2030'da yıllık ~€120K tasarruf demek.",
    ],
    body_en: [
      "**Challenge**: The customer started receiving mandatory CBAM report requests from EU importers as of January 2026. Three separate production lines required independent embedded-emission calculations. Existing steel production capacity: 6,000 t/year, all products under CN 7206/7207/7213.",
      "**Solution**: We executed the following steps on Enasuspro® Carbon Ledger:",
      "**Week 1-2**: Facility setup (3 facilities, 3 production lines). ISO 14064-1 compliant Scope 1 (coke + natural gas) and Scope 2 (purchased electricity) factors defined. Base year 2025.",
      "**Week 3-4**: Bulk import of 12 months of historical data (CSV). Measured direct emissions 2.15 tCO₂e/t and indirect 0.42 tCO₂e/t — higher than EU Annex III defaults (2.0 + 0.4), which forced the client to provide plant-specific data.",
      "**Week 5**: First quarterly declarations (Q1 2026) entered on the CBAM page. 1,500 t · Türkiye origin, origin carbon price paid = 0 (Türkiye ETS pilot not yet active).",
      "**Week 6**: XML export uploaded to the EU CBAM Registry portal as a test. PDF delivered to the client's board. Outcome: **€8,632 CBAM liability** — with 2026 phase-in of 2.5%. In 2030 this rises 20× to €172,640/quarter.",
      "**Result & insight**: The customer decided to migrate the 2027 electricity supply contract to a green PPA — the calculation showed renewable electricity would cut total embedded emissions **by 18%**, equating to ~€120K/year savings in 2030.",
    ],
  },
  {
    slug: "csrd-esrs-2027-hazirlik",
    icon: Buildings,
    category_tr: "Rehber",
    category_en: "Guide",
    read_tr: "8 dk okuma",
    read_en: "8 min read",
    date: "2026-02-10",
    title_tr: "500+ çalışanlı Türk şirketi için CSRD/ESRS 2027 hazırlığı",
    title_en: "CSRD/ESRS 2027 readiness for 500+ employee Turkish companies",
    excerpt_tr: "CSRD, Türk şirketlerini de dolaylı olarak 2027-2028'de etkileyecek. Enasuspro® ekibi olarak müşterilerimize önerdiğimiz 12-aylık hazırlık takvimi.",
    excerpt_en: "CSRD will indirectly affect Turkish companies too from 2027-2028. Our recommended 12-month preparation calendar.",
    body_tr: [
      "Kurumsal Sürdürülebilirlik Raporlama Direktifi (CSRD), AB'de faaliyet gösteren büyük şirketler için 2024'ten itibaren yürürlükte. Türk şirketleri için doğrudan bağlayıcı olmasa da: (1) AB'de iştiraki olan Türk grupları, (2) AB'ye ihracat yapan tedarikçiler, (3) AB'de listeli müşterileri olan şirketler dolaylı olarak etkilenecek.",
      "**ESRS'in 12 tematik standardı**: E1 İklim, E2 Kirlilik, E3 Su, E4 Biyoçeşitlilik, E5 Döngüsel, S1-S4 Sosyal, G1 Yönetişim + 2 evrensel standart (ESRS 1, 2). Enasuspro® Carbon Ledger 10 tematik standardı doğrudan destekler.",
      "**12 aylık hazırlık takvimi**:",
      "**Ay 1-3 · Materiality assessment**: Çift önemlilik (double materiality) analizi — hangi konular hem şirketinizi hem paydaşları önemli şekilde etkiliyor?",
      "**Ay 4-6 · Veri toplama altyapısı**: ISO 14064 uyumlu Kapsam 1/2/3 hesaplama sistemleri. Enasuspro® ile ~4 hafta.",
      "**Ay 7-9 · Nitel açıklamalar**: E2/E3/E4/E5 + S1-S4 + G1 için politika, aksiyon, hedef, KPI, mali etki açıklamaları.",
      "**Ay 10-11 · İç değerlendirme + boşluk analizi**: EFRAG XBRL taksonomisiyle otomatik doğrulama.",
      "**Ay 12 · Bağımsız güvence**: ISAE 3000 / ISO 14064-3 sertifikalı denetçi ile sınırlı güvence.",
      "**Sonuç**: Enasuspro® ile bu süreç ortalama %40 hızlanır ve maliyet %30 azalır — çünkü E1 climate datası CBAM ile paylaşımlı yönetilir, GRI 305 ile otomatik cross-map yapılır.",
    ],
    body_en: [
      "The Corporate Sustainability Reporting Directive (CSRD) has been in force for large EU-based companies since 2024. Although not directly binding on Turkish companies, they will be indirectly affected via: (1) Turkish groups with EU subsidiaries, (2) suppliers exporting to the EU, (3) companies with EU-listed customers.",
      "**ESRS's 12 topical standards**: E1 Climate, E2 Pollution, E3 Water, E4 Biodiversity, E5 Circular Economy, S1-S4 Social, G1 Governance + 2 universal standards (ESRS 1, 2). Enasuspro® Carbon Ledger directly supports 10 topical standards.",
      "**12-month preparation calendar**:",
      "**Month 1-3 · Materiality assessment**: Double-materiality analysis — which topics significantly affect both your company AND stakeholders?",
      "**Month 4-6 · Data collection infrastructure**: ISO 14064-compliant Scope 1/2/3 calculation systems. ~4 weeks with Enasuspro®.",
      "**Month 7-9 · Qualitative disclosures**: Policies, actions, targets, KPIs, financial-effect statements for E2/E3/E4/E5 + S1-S4 + G1.",
      "**Month 10-11 · Internal review + gap analysis**: Automated validation via EFRAG XBRL taxonomy.",
      "**Month 12 · Independent assurance**: Limited assurance by an ISAE 3000 / ISO 14064-3 certified auditor.",
      "**Outcome**: With Enasuspro® this process becomes ~40% faster and ~30% cheaper — because E1 climate data is shared with CBAM and auto-mapped to GRI 305.",
    ],
  },
  {
    slug: "danismanlik-firma-portfoy-yonetimi",
    icon: Handshake,
    category_tr: "Vaka Çalışması",
    category_en: "Case Study",
    read_tr: "5 dk okuma",
    read_en: "5 min read",
    date: "2026-01-28",
    title_tr: "12 müşterili sürdürülebilirlik danışmanlığı — portföy yönetimi hikayesi",
    title_en: "Sustainability consultancy with 12 clients — a portfolio management story",
    excerpt_tr: "Bir Enasuspro® ekibi 12 aktif müşteriyle çalışıyor. Excel + PowerPoint kaosu, tek platforma dönüştü.",
    excerpt_en: "An Enasuspro® team is running 12 active clients. Excel + PowerPoint chaos got consolidated into one platform.",
    body_tr: [
      "**Başlangıç durumu**: 12 müşteri kuruluşu için 12 ayrı Excel dosyası, 12 ayrı PowerPoint sunumu. Her yeni müşteride onboarding 3-4 hafta sürüyordu. Danışmanlar zamanının %60'ını 'raporlama işleri' ile geçiriyordu.",
      "**Enasuspro® Müşteri Portalı ile dönüşüm**:",
      "**Tek pano**: 12 müşterinin **onboarding % ilerleme çubuğu**, bekleyen onaylar, portföy emisyonu (7.240 tCO₂e), tam onboard 8/12. Sabah kahvenizle 30 saniyede genel resim.",
      "**5-adım onboarding checklist**: Yeni müşteri: Profil → Tesis → Emisyon → Hedef → İlk Rapor. Her adım %20 doldurur. Danışman ilerlemeyi müşteriye WhatsApp linki ile gösterir.",
      "**Otomatik davet e-postası** (Resend API): Müşteri kullanıcısına Enasuspro® yeşil temalı hoşgeldin maili + geçici parola.",
      "**Zaman tasarrufu**: Onboarding süresi 3-4 haftadan 1 haftaya düştü. Danışmanlar zamanının %70'ini 'strateji ve içgörü' işine ayırabiliyor.",
      "**İş sonucu**: Aynı ekiple 3 ay içinde 5 yeni müşteri kabul edildi. Firma cirosu %62 arttı, danışman başına verim %90.",
    ],
    body_en: [
      "**Starting point**: 12 separate Excel files and 12 PowerPoint presentations for 12 client organizations. Each new client onboarding took 3-4 weeks. Consultants spent 60% of their time on 'reporting work'.",
      "**Transformation with the Enasuspro® Client Portal**:",
      "**Single dashboard**: **Onboarding % progress bars** for 12 clients, pending approvals, portfolio emissions (7,240 tCO₂e), 8/12 fully onboarded. Full picture in 30 seconds over morning coffee.",
      "**5-step onboarding checklist**: For new client: Profile → Facility → Emissions → Target → First Report. Each step fills 20%. Consultants share progress with clients via WhatsApp link.",
      "**Automated invitation email** (Resend API): Enasuspro® green-themed welcome email + temporary password.",
      "**Time savings**: Onboarding dropped from 3-4 weeks to 1 week. Consultants now spend 70% of their time on 'strategy & insight' work.",
      "**Business outcome**: Same team accepted 5 new clients in 3 months. Firm revenue up 62%, productivity per consultant +90%.",
    ],
  },
];

export default function Blog() {
  const { lang, setLang } = useLang();
  const { user } = useAuth();
  const isTR = lang === "tr";
  const T = (tr, en) => (isTR ? tr : en);
  const [openSlug, setOpenSlug] = React.useState(null);

  const open = POSTS.find((p) => p.slug === openSlug);

  return (
    <div className="min-h-screen bg-background grain-bg">
      {/* Header */}
      <header className="border-b border-border bg-background/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-md bg-primary text-primary-foreground flex items-center justify-center">
              <Leaf size={20} weight="fill" />
            </div>
            <div>
              <div className="font-display font-semibold">Enasuspro® Carbon Ledger</div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-widest">Insights & Case Studies</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <div className="inline-flex rounded-md border border-border overflow-hidden text-xs">
              <button onClick={() => setLang("tr")} className={`px-3 py-1.5 font-mono-data ${lang === "tr" ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}>TR</button>
              <button onClick={() => setLang("en")} className={`px-3 py-1.5 font-mono-data ${lang === "en" ? "bg-primary text-primary-foreground" : "hover:bg-secondary"}`}>EN</button>
            </div>
            <Link to={user ? "/app" : "/register"}>
              <Button size="sm">{user ? T("Panele Git", "Go to App") : T("Ücretsiz Başla", "Start Free")}</Button>
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-6 py-16">
        {!open ? (
          <>
            <div className="max-w-2xl mb-12">
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-mono-data">Blog · Vaka Çalışması</div>
              <h1 className="font-display text-5xl font-semibold mt-2">
                {T("Sürdürülebilirlik hikayelerinden ", "Stories from sustainability ")}
                <span className="text-primary">{T("içgörüler.", "in practice.")}</span>
              </h1>
              <p className="text-muted-foreground mt-4">{T("Enasuspro® müşterileriyle yaşanmış CBAM, ESRS, GRI dönüşüm hikayeleri.", "Real CBAM, ESRS, GRI transformation stories from Enasuspro® clients.")}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-list">
              {POSTS.map((p) => {
                const Icon = p.icon;
                return (
                  <div key={p.slug} className="border border-border rounded-md p-6 hover:border-primary/50 transition-colors cursor-pointer group" onClick={() => setOpenSlug(p.slug)} data-testid={`blog-card-${p.slug}`}>
                    <div className="w-10 h-10 rounded-md bg-primary/10 text-primary flex items-center justify-center mb-4 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                      <Icon size={20} weight="duotone" />
                    </div>
                    <div className="text-[10px] uppercase tracking-widest font-mono-data text-primary mb-2">{T(p.category_tr, p.category_en)}</div>
                    <h3 className="font-display text-lg font-semibold leading-tight">{T(p.title_tr, p.title_en)}</h3>
                    <p className="text-sm text-muted-foreground mt-2 line-clamp-3">{T(p.excerpt_tr, p.excerpt_en)}</p>
                    <div className="text-xs text-muted-foreground font-mono-data mt-4 flex items-center gap-3">
                      <span className="flex items-center gap-1"><Clock size={12} />{T(p.read_tr, p.read_en)}</span>
                      <span>{p.date}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <article data-testid={`blog-article-${open.slug}`}>
            <button onClick={() => setOpenSlug(null)} className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-6" data-testid="blog-back">
              <ArrowLeft size={14} /> {T("Tüm yazılara dön", "Back to all posts")}
            </button>
            <div className="text-[10px] uppercase tracking-widest font-mono-data text-primary mb-2">{T(open.category_tr, open.category_en)}</div>
            <h1 className="font-display text-4xl font-semibold leading-tight mb-4">{T(open.title_tr, open.title_en)}</h1>
            <div className="text-xs text-muted-foreground font-mono-data flex items-center gap-4 mb-8">
              <span className="flex items-center gap-1"><Clock size={12} />{T(open.read_tr, open.read_en)}</span>
              <span>{open.date}</span>
            </div>
            <div className="prose prose-sm max-w-none space-y-4">
              {T(open.body_tr, open.body_en).map((para, i) => (
                <p key={i} className="text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: para.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>") }} />
              ))}
            </div>
            <div className="mt-12 border-t border-border pt-8">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <div className="text-xs uppercase tracking-widest font-mono-data text-muted-foreground">{T("Benzer bir hikayeye başlamaya hazır mısınız?", "Ready to start a similar story?")}</div>
                  <div className="font-display text-xl mt-1">{T("İlk 14 gün ücretsiz.", "First 14 days free.")}</div>
                </div>
                <Link to={user ? "/app" : "/register"}>
                  <Button size="lg"><Sparkle size={16} className="mr-2" weight="fill" />{user ? T("Panele Git", "Go to App") : T("Ücretsiz Başla", "Start Free")}<ArrowRight size={16} className="ml-2" /></Button>
                </Link>
              </div>
            </div>
          </article>
        )}
      </div>

      <footer className="border-t border-border">
        <div className="max-w-5xl mx-auto px-6 py-6 flex items-center justify-between text-xs text-muted-foreground font-mono-data">
          <span>© {new Date().getFullYear()} Enasuspro® Sustainability Coaching</span>
          <Link to="/" className="hover:text-primary">← {T("Ana sayfa", "Home")}</Link>
        </div>
      </footer>
    </div>
  );
}
